import React, { useState } from 'react';
import _t from 'counterpart';
import CIcon from '@coreui/icons-react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CRow,
} from '@coreui/react';
import { capitalize, extractErrorMessage } from '../../helpers';
import {
  deleteMt5Wallet,
  disableWallet,
  enableWallet,
  loadCustomerWallets,
  resetMt5Password,
  revealMt5Password,
} from '../../services/BackendService';
import AddWalletModal from './AddWalletModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import CustomerWalletPopup from './CustomerWalletPopup';
import Mt5CredentialsModal from './Mt5CredentialsModal';
import PlatformBadge from '../../components/PlatformBadge';
import Balance from './CustomerWalletBalance';
import Credit from './CustomerWalletCredit';
import { isGatewayWallet, IWallet, WalletStatus } from '../wallets/types';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../helpers/customHooks';
import { Permission } from '../../reducers/userReducer';
import WalletTransferModal from './WalletTransferModal';
import toast from 'react-hot-toast';
import { TradingType, WalletType } from './types';
import { isWalletAllowedToTransfer } from './helpers';

/**
 * Balance and credit are fetched per wallet from `/admin/wallets/:id/balance`, which now answers
 * for both platforms — Wintrado from the trading gateway, MT5 from the mt5-bridge. It still only
 * answers for active accounts, so a disabled one reads 'n/a' regardless of platform.
 */
const walletAmount = (wallet: IWallet, amount: JSX.Element) =>
  wallet.status === WalletStatus.Active ? amount : 'n/a';

function CustomerWalletsTable({ customerId }: { customerId: string }) {
  const [walletPopup, setWalletPopup] = useState<IWallet | null>(null);
  const [addWalletModalShow, toggleAddWalletVisibility] = useState<boolean>(false);
  const [walletIdToEnable, setWalletIdToEnable] = useState<number | null>(null);
  const [walletIdToDisable, setWalletIdToDisable] = useState<number | null>(null);
  const [transferModalShow, setTransferModalShow] = useState<boolean>(false);
  const [walletToDelete, setWalletToDelete] = useState<IWallet | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<{ walletId: number; password: string } | null>(null);

  const permissions = useAppSelector((state) => state.user.permissions);

  const { data, isLoading } = useQuery(['customer-wallets', customerId], async () => {
    const data = await loadCustomerWallets(customerId);
    return data;
  });

  const queryClient = useQueryClient();

  const manageWalletsPermission = permissions?.includes(Permission.MANAGE_WALLETS);
  const revealMt5PasswordPermission = permissions?.includes(Permission.REVEAL_MT5_PASSWORD);

  const showError = (e: unknown): void => {
    toast.error(extractErrorMessage(e));
  };

  const deleteMt5WalletMutation = useMutation((id: number) => deleteMt5Wallet(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('customer-wallets');
      toast.success(_t('wallet.mt5-account-deleted'));
    },
    // Covers the has-open-trades conflict, which the backend returns as a 409 with the reason.
    onError: showError,
    onSettled: () => setWalletToDelete(null),
  });

  /**
   * Reset and reveal differ only in which endpoint produces the password, and both end in the same
   * credentials modal, so they share one mutation keyed by the call to make.
   */
  const mt5PasswordMutation = useMutation(
    ({ id, action }: { id: number; action: (walletId: number) => Promise<string> }) => action(id),
    {
      onSuccess: (password: string, { id }) => setRevealedPassword({ walletId: id, password }),
      onError: showError,
    }
  );

  const showTransferModal = () => {
    setTransferModalShow(true);
  };

  const hideTransferModal = () => {
    setTransferModalShow(false);
  };

  const walletsLengthLimit = data ? data.filter((w) => isWalletAllowedToTransfer(w)).length : 0;

  const enableWalletMutation = useMutation(['enable-wallet'], (id: number) => enableWallet(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('customer-wallets');
      setWalletIdToEnable(null);
    },
    onError: (e: unknown) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const disableWalletMutation = useMutation(['enable-wallet'], (id: number) => disableWallet(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('customer-wallets');
      setWalletIdToDisable(null);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const tryEnableWallet = () => {
    if (walletIdToEnable) {
      enableWalletMutation.mutate(walletIdToEnable);
    }
  };

  const tryDisableWallet = () => {
    if (walletIdToDisable) {
      const wallet = data?.find((w) => w.id === walletIdToDisable);

      if (wallet?.type === WalletType.PropChallenge && wallet.status === WalletStatus.Active) {
        toast.error(_t('wallet.prop-challenge-cannot-disable'));
        setWalletIdToDisable(null);
        return;
      }

      disableWalletMutation.mutate(walletIdToDisable);
    }
  };

  return (
    <CRow>
      <CCol md={12}>
        <CCard className="card card--listing card--dropdown-overflow">
          <CCardBody className="card__holder">
            <CCardHeader className="card__header">
              <div className="card__info">
                <h4 className="card__title">{_t('customer.wallets.title')}</h4>
              </div>
              {manageWalletsPermission && (
                <div>
                  {walletsLengthLimit > 1 && (
                    <CButton
                      color="primary"
                      className="mr-2 header-button-transfer"
                      onClick={showTransferModal}
                      title={_t('customer.transfer.transfer')}
                    />
                  )}
                  <CButton
                    data-testid="add-wallet-button"
                    color="primary"
                    className="mr-2 header-button-add"
                    onClick={() => toggleAddWalletVisibility(true)}
                  />
                </div>
              )}
            </CCardHeader>
            <CDataTable
              loading={isLoading}
              items={data || []}
              fields={[
                { key: 'status', label: capitalize(_t('global.status')) },
                { key: 'isDemo', label: _t('global.type') },
                { key: 'info', label: capitalize(_t('customer.wallets.accountId')) },
                { key: 'platform', label: capitalize(_t('customer.wallets.platform')) },
                { key: 'currency', label: capitalize(_t('customer.wallets.currency')) },
                { key: 'balance', label: capitalize(_t('customer.wallets.balance')) },
                { key: 'credit', label: capitalize(_t('customer.wallets.credit')) },
                { key: 'group', label: capitalize(_t('wallet-modal.group')) },
                { key: 'tradingType', label: _t('wallets.trading-type') },
                { key: 'actions', label: null },
              ]}
              scopedSlots={{
                info: (wallet: IWallet) => (
                  <td>
                    {manageWalletsPermission && (
                      <Link to={`/wallets/${wallet.id}`}>
                        <div>{wallet.id}</div>
                        <div className="small text-medium-emphasis">{wallet.type}</div>
                      </Link>
                    )}
                    {!manageWalletsPermission && (
                      <>
                        <div>{wallet.id}</div>
                        <div className="small text-medium-emphasis">{wallet.type}</div>
                      </>
                    )}
                  </td>
                ),
                tradingType: (wallet: IWallet) => (
                  <td>
                    {wallet.type === WalletType.Affiliate ? (
                      '-'
                    ) : (
                      <CBadge
                        style={{ backgroundColor: wallet.tradingType === TradingType.Netting ? '#18B8C2' : '#9618C2' }}
                      >
                        {wallet.tradingType === TradingType.Netting ? _t('wallets.netting') : _t('wallets.hedging')}
                      </CBadge>
                    )}
                  </td>
                ),
                isDemo: (wallet: IWallet) => (
                  <td>
                    <CBadge color={wallet.isDemo ? 'danger' : 'success'}>
                      {wallet.isDemo ? _t('global.demo') : _t('global.real')}
                    </CBadge>
                  </td>
                ),
                balance: (wallet: IWallet) => (
                  <td style={{ width: '10%' }}>{walletAmount(wallet, <Balance walletId={wallet.id} />)}</td>
                ),
                credit: (wallet: IWallet) => (
                  <td style={{ width: '10%' }}>{walletAmount(wallet, <Credit walletId={wallet.id} />)}</td>
                ),
                platform: (wallet: IWallet) => (
                  <td>
                    <PlatformBadge platform={wallet.platform} />
                  </td>
                ),
                group: (wallet: IWallet) => <td>{wallet.type === WalletType.Affiliate ? '-' : wallet.group?.name}</td>,
                status: (wallet: IWallet) => (
                  <td>
                    <CBadge color={wallet.status.toLowerCase()}>{wallet.status}</CBadge>
                    {wallet.primary && <br />}
                    {wallet.primary && <CBadge color="primary">PRIMARY</CBadge>}
                  </td>
                ),
                actions: (wallet: IWallet) => (
                  <td>
                    {manageWalletsPermission && wallet.type !== WalletType.Affiliate && (
                      <CDropdown>
                        <CDropdownToggle size="md" color="primary" caret={false}>
                          <CIcon name="cil-pencil" size="sm" />
                        </CDropdownToggle>
                        <CDropdownMenu className="py-0" placement="bottom-end">
                          <CDropdownItem onClick={() => setWalletPopup(wallet)}>{_t('action.edit')}</CDropdownItem>

                          {wallet.status === WalletStatus.Active && (
                            <CDropdownItem onClick={() => setWalletIdToDisable(wallet.id)}>
                              {_t('wallet.disable-wallet')}
                            </CDropdownItem>
                          )}

                          {wallet.status === WalletStatus.Disabled && (
                            <CDropdownItem onClick={() => setWalletIdToEnable(wallet.id)}>
                              {_t('wallet.enable-wallet')}
                            </CDropdownItem>
                          )}

                          {!isGatewayWallet(wallet) && (
                            <CDropdownItem
                              onClick={() => mt5PasswordMutation.mutate({ id: wallet.id, action: resetMt5Password })}
                            >
                              {_t('wallet.mt5-reset-password')}
                            </CDropdownItem>
                          )}

                          {!isGatewayWallet(wallet) && revealMt5PasswordPermission && (
                            <CDropdownItem
                              onClick={() => mt5PasswordMutation.mutate({ id: wallet.id, action: revealMt5Password })}
                            >
                              {_t('wallet.mt5-reveal-password')}
                            </CDropdownItem>
                          )}

                          {!isGatewayWallet(wallet) && (
                            <CDropdownItem onClick={() => setWalletToDelete(wallet)}>
                              {_t('wallet.mt5-delete-account')}
                            </CDropdownItem>
                          )}
                        </CDropdownMenu>
                      </CDropdown>
                    )}
                  </td>
                ),
              }}
              hover
              striped
            />
          </CCardBody>
        </CCard>
      </CCol>

      {manageWalletsPermission && (
        <AddWalletModal
          show={addWalletModalShow}
          onClose={() => toggleAddWalletVisibility(false)}
          customerId={customerId}
        />
      )}

      {walletPopup && <CustomerWalletPopup wallet={walletPopup} onClose={() => setWalletPopup(null)} />}

      {walletsLengthLimit > 1 && (
        <WalletTransferModal show={transferModalShow} onClose={hideTransferModal} customerId={customerId} />
      )}

      <ConfirmationModal
        id=""
        show={walletIdToEnable !== null}
        onConfirm={tryEnableWallet}
        hidePanel={() => setWalletIdToEnable(null)}
        title={_t('wallet.enable-wallet')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={false}
      />

      <ConfirmationModal
        id=""
        show={walletIdToDisable !== null}
        onConfirm={tryDisableWallet}
        hidePanel={() => setWalletIdToDisable(null)}
        title={_t('wallet.disable-wallet')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={false}
      />

      <ConfirmationModal
        id=""
        show={walletToDelete !== null}
        onConfirm={() => walletToDelete && deleteMt5WalletMutation.mutate(walletToDelete.id)}
        hidePanel={() => setWalletToDelete(null)}
        title={_t('wallet.mt5-delete-account')}
        description={_t('wallet.mt5-delete-confirmation', { id: walletToDelete?.id })}
        error={null}
        buttonColor="danger"
        withMutation={false}
        isLoading={deleteMt5WalletMutation.isLoading}
      />

      {revealedPassword && (
        <Mt5CredentialsModal {...revealedPassword} onClose={() => setRevealedPassword(null)} />
      )}
    </CRow>
  );
}

export default CustomerWalletsTable;
